import { Avatar, Badge, Box, Group, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { timeAgo } from '../../lib/chat-format';
import type { ChannelCapabilities, InboxListItem } from './types';

/** A single conversation-list row — avatar, title, subtitle, last-message time,
 *  unread badge. Optional WA-only affordances (label bg, draft) render when the
 *  adapter populates them. */
export function ConversationRow({
  item,
  active,
  capabilities,
  onSelect,
}: {
  item: InboxListItem;
  active: boolean;
  capabilities: ChannelCapabilities;
  onSelect: () => void;
}) {
  const Icon = capabilities.channelIcon;
  return (
    <motion.button
      whileHover={{ backgroundColor: 'var(--mantine-color-gray-1)' }}
      style={{
        background: active ? 'var(--mantine-color-blue-light)' : 'transparent',
        borderLeft: item.labelColor ? `3px solid ${item.labelColor}` : '3px solid transparent',
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        padding: 12,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
      onClick={onSelect}
    >
      <Group wrap="nowrap" gap="sm">
        <Avatar size="md" radius="xl" src={item.avatarUrl ?? undefined}>
          <Icon size={14} />
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" fw={600} truncate>
              {item.title}
            </Text>
            {item.lastMessageAt && (
              <Text size="xs" c="dimmed">
                {timeAgo(new Date(item.lastMessageAt))}
              </Text>
            )}
          </Group>
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" c="dimmed" truncate>
              {item.draftText
                ? `Draft: ${item.draftText}`
                : (item.lastMessage ?? item.subtitle ?? '')}
            </Text>
            {item.unreadCount > 0 && (
              <Badge size="xs" color={capabilities.accent}>
                {item.unreadCount}
              </Badge>
            )}
          </Group>
        </Box>
      </Group>
    </motion.button>
  );
}
